/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import humanize from 'humanize-plus';
import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import GetAppIcon from '@mui/icons-material/GetApp';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Chip, CircularProgress, Grid, IconButton, Typography } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';

import { ClusterStates } from 'shared';

import { AlignCenter } from '../../../components/AlignCenter/AlignCenter';
import { useEventCallback } from '../../../components/hooks/useEventCallback';
import { Namespaces } from '../../../i18n';
import { useTableStyles } from '../../../styles/table';
import { ClusterNodeListResponsePayload } from '../../../types/cluster/ClusterNodeListResponsePayload';
import { UpdateClusterDetailsParameterIdentification } from '../../../types/cluster/UpdateClusterDetailsParameterIdentification';
import { determineNodeStatusColor } from '../../../utils/determineNodeStatusColor';
import { fetchNodeLogsOfCluster } from '../../../utils/queryFns';
import { useProjectPermissions } from '../../../utils/useProjectPermissions';
import { NodeStatus } from './NodeStatus';
import { Accordion, AccordionDetails, AccordionSummary, createStatusTabStyles } from './ReplicasTab/ReplicasTab.styles';
import { DownloadableNodeId, HandlePopoverToggleArgs, HandleRestartModalArgs } from './ReplicasTab/ReplicasTab.types';

type NodeExpansionPanelProps = {
  node: ClusterNodeListResponsePayload;
  identificationData: UpdateClusterDetailsParameterIdentification;
  handlePopoverToggle: (args: HandlePopoverToggleArgs) => void;
  handleRestartModal: (args: HandleRestartModalArgs) => void;
  clusterStatus: ClusterStates | undefined;
  isDownloadingLogs: boolean;
};

export function NodeExpansionPanel(props: NodeExpansionPanelProps) {
  const { t } = useTranslation(Namespaces.Cluster);
  const { node, identificationData, handlePopoverToggle, handleRestartModal, clusterStatus } = props;
  const classes = createStatusTabStyles();
  const tableClasses = useTableStyles();

  const permissions = useProjectPermissions(identificationData.organizationId, identificationData.projectId);

  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const [logsWrapper, setLogsWrapper] = useState<HTMLElement | null>(null);
  const [isScrolled, setIsScrolled] = useState<boolean>(false);

  const scrollLogsToBottom = useEventCallback(() => {
    if (!isScrolled && logsWrapper) {
      logsWrapper.scrollTop = logsWrapper.scrollHeight;
    }
  });

  useEffect(() => {
    if (!logsWrapper) {
      return;
    }
    logsWrapper.scrollTop = logsWrapper.scrollHeight;
    const subscriber = () => {
      const { scrollTop, clientHeight, scrollHeight } = logsWrapper;
      setIsScrolled(!(scrollHeight === clientHeight || clientHeight + scrollTop >= scrollHeight));
    };
    logsWrapper.addEventListener('scroll', subscriber);
    return () => {
      logsWrapper.removeEventListener('scroll', subscriber);
    };
  }, [logsWrapper]);

  const nodeLogsQuery = useQuery(
    [
      fetchNodeLogsOfCluster.key,
      {
        ...identificationData,
        nodeId: node.id,
      },
    ],
    fetchNodeLogsOfCluster.fn,
  );

  const nodeLogs = useMemo(() => nodeLogsQuery.data?.payload || '', [nodeLogsQuery.data]);

  useLayoutEffect(() => {
    scrollLogsToBottom();
  }, [scrollLogsToBottom, nodeLogs]);

  const handleOpenPopover = ({ id, event }: { id: DownloadableNodeId; event: React.MouseEvent<HTMLButtonElement> }) => {
    event.stopPropagation();
    handlePopoverToggle({ event, id });
  };

  const onRestartClick = (args: HandleRestartModalArgs) => {
    handleRestartModal(args);
  };

  const ableToRestart = clusterStatus === ClusterStates.Running && node.status === NodeStatus.running;

  return nodeLogsQuery.status === 'loading' ? (
    <AlignCenter>
      <CircularProgress color="primary" size={40} />
    </AlignCenter>
  ) : (
    <Accordion square TransitionProps={{ unmountOnExit: true }} expanded={isExpanded}>
      <AccordionSummary
        onClick={() => setIsExpanded(s => !s)}
        expandIcon={
          <Tooltip
            title={
              isExpanded
                ? t(`${Namespaces.Cluster}:details.replicas.hide_logs_tooltip`)!
                : t(`${Namespaces.Cluster}:details.replicas.show_logs_tooltip`)!
            }
          >
            <span className={classes.arrowIcon} />
          </Tooltip>
        }
      >
        <Grid container>
          <Grid item xs={2} classes={{ root: classes.gridItem }}>
            <Typography className={tableClasses.bodyTableCellText}>
              {t(`${Namespaces.Cluster}:details.replicas.instance`)} #{node.id}
            </Typography>
          </Grid>
          <Grid item xs={2} classes={{ root: classes.gridItem }}>
            <Typography className={tableClasses.bodyTableCellText}>
              {t(`${Namespaces.Cluster}:details.replicas.roles.${node.role.toLowerCase()}`)}
            </Typography>
          </Grid>
          <Grid item xs={2} classes={{ root: classes.gridItem }}>
            <Typography className={tableClasses.bodyTableCellText}>{node.lag || <>&mdash;</>}</Typography>
          </Grid>
          <Grid item xs={2} classes={{ root: classes.gridItem }}>
            <Chip
              classes={{ root: classes.chip }}
              label={t(`${Namespaces.Entities}:cluster.patroni_status.values.${node.status.toLowerCase()}`)}
              style={{
                background: determineNodeStatusColor(node.status),
              }}
            />
          </Grid>
          <Grid item xs={2} classes={{ root: classes.gridItem }}>
            <Typography className={tableClasses.bodyTableCellText}>
              {t(`${Namespaces.Cluster}:details.replicas.storage_usage`, {
                used: humanize.fileSize(node.storage.usedRaw * Math.pow(1024, 2), 0),
                usedPercent: parseInt(node.storage.usedPercent),
                initial: humanize.fileSize((node.storage.usedRaw + node.storage.available) * Math.pow(1024, 2), 0),
              })}
            </Typography>
          </Grid>
          <Grid item xs={2} classes={{ root: classes.gridItem }} className={classes.dlIconWrap}>
            {permissions.edit_cluster ? (
              <Tooltip
                title={
                  !ableToRestart
                    ? t('details.replicas.restart_tooltip_disabled')!
                    : t('details.replicas.restart_tooltip')!
                }
              >
                <span className={classes.restartBtn} onClick={e => e.stopPropagation()}>
                  <IconButton
                    id={node.id}
                    onClick={() => onRestartClick({ id: node.id, role: node.role })}
                    size="medium"
                    disabled={!ableToRestart}
                    data-cy="cluster-page-restart-node-button"
                  >
                    <RefreshIcon />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
            <Tooltip title={t('details.replicas.download_tooltip')!}>
              {props.isDownloadingLogs ? (
                <CircularProgress size={24} />
              ) : (
                <IconButton
                  id={node.id}
                  onClick={event => handleOpenPopover({ id: node.id, event })}
                  size="medium"
                  data-cy="cluster-page-nodes-tab-download-logs-button"
                >
                  <GetAppIcon />
                </IconButton>
              )}
            </Tooltip>
          </Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container direction="column" wrap="nowrap">
          <Grid item xs={12}>
            <div className={classes.logsContainer}>
              <Typography className={classes.logs} component="div" ref={setLogsWrapper}>
                <pre className={classes.logsPre}>{nodeLogs}</pre>
              </Typography>
            </div>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
}
